<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header: вкладки слева (со счётчиками, чтобы не прыгали и было видно
         содержимое обеих), «Найдено» + фильтр справа; кнопки добавления —
         строкой ниже слева (не зависят от ширины и не двигают вкладки). -->
    <div class="d-flex align-center mb-1" style="min-height: 40px;">
      <v-tabs :model-value="tab" density="compact" color="primary" align-tabs="start" @update:model-value="setTab">
        <v-tab value="telegram">Телеграм ({{ telegramCount }})</v-tab>
        <v-tab value="social">Соцсети ({{ socialCount }})</v-tab>
        <v-tab value="web">Сайты ({{ webCount }})</v-tab>
        <v-tab value="search">Поисковые системы ({{ searchCount }})</v-tab>
      </v-tabs>
      <v-spacer></v-spacer>
      <span class="text-caption font-weight-bold mr-2">Найдено: {{ total }}</span>
      <v-btn
        :icon="panelVisible ? 'mdi-filter-off' : 'mdi-filter'"
        variant="outlined"
        size="small"
        @click="panelVisible = !panelVisible"
        :color="panelVisible ? 'primary' : 'default'"
      ></v-btn>
    </div>
    <div class="d-flex align-center ga-2 mb-2">
      <template v-if="tab === 'web'">
        <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="openWebAdd">
          Добавить сайт
        </v-btn>
        <HelpHint section="sources" anchor="sites" text="Как добавить сайт и что значат его статусы" />
      </template>
      <template v-else-if="tab === 'search'">
        <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="openSearchAdd">
          Добавить запрос
        </v-btn>
        <HelpHint section="sources" anchor="search" text="Запросы для Google и Яндекса: длина, глубина, настройки" />
      </template>
      <template v-else>
        <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="openAddDialog">
          Добавить
        </v-btn>
        <v-btn color="secondary" variant="outlined" prepend-icon="mdi-plus-box-multiple" @click="openBulkDialog">
          Добавить несколько
        </v-btn>
      </template>
    </div>

    <v-row>
      <!-- Table -->
      <v-col :cols="panelVisible ? 10 : 12" class="table-column-transition">
        <v-card>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="tab === 'search' ? searchGroups : sources"
              :items-per-page="-1"
              :loading="isLoading"
              :show-expand="tab === 'search'"
              v-model:expanded="expandedSearch"
              item-value="id"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 236px)"
              hide-default-footer
            >
              <!-- Поисковые системы: одна строка на запрос, метки поисковиков со статусом -->
              <template v-slot:[`item.engines`]="{ item }">
                <v-tooltip v-for="rec in item.items" :key="rec.id" :text="engineChipTooltip(rec)" location="top">
                  <template #activator="{ props }">
                    <v-chip v-bind="props" size="small" variant="tonal" :color="engineChipColor(rec)" class="mr-1 mb-1">
                      {{ platformDisplayName(rec.platform_name) }}
                    </v-chip>
                  </template>
                </v-tooltip>
              </template>
              <template v-slot:expanded-row="{ columns, item }">
                <tr v-for="rec in item.items" :key="rec.id" class="search-group-row">
                  <td :colspan="columns.length">
                    <div class="d-flex align-center flex-wrap ga-2 py-1">
                      <v-chip size="small" variant="tonal" :color="engineChipColor(rec)">{{ platformDisplayName(rec.platform_name) }}</v-chip>
                      <span class="text-caption text-grey">глубина {{ (rec.extra_attributes && rec.extra_attributes.search_depth) || '—' }}</span>
                      <span class="text-caption" :class="{ 'text-primary cursor-pointer': getMessagesCount(rec) > 0 }" @click="getMessagesCount(rec) > 0 && openMessagesModal(rec)">сообщений {{ formatNumber(getMessagesCount(rec)) }}</span>
                      <span class="text-caption text-grey">обновлено {{ getLastContentAt(rec) ? formatISODate(getLastContentAt(rec)) : '—' }}</span>
                      <v-chip :color="statusColor(rec.status)" size="x-small" variant="tonal">{{ statusLabel(rec.status, rec.source_type) }}</v-chip>
                      <v-spacer></v-spacer>
                      <template v-if="isAdmin">
                        <v-btn v-if="rec.is_monitoring" icon="mdi-stop" size="x-small" variant="text" color="warning" title="Остановить" @click="stopMonitoring(rec)"></v-btn>
                        <v-btn v-else icon="mdi-play" size="x-small" variant="text" color="success" title="Запустить" :disabled="!rec.source_id" @click="startMonitoring(rec)"></v-btn>
                      </template>
                      <v-btn icon="mdi-pencil" size="x-small" variant="text" title="Настройки поисковика" @click="openEditDialog(rec)"></v-btn>
                      <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" title="Убрать поисковик" @click="confirmDelete(rec)"></v-btn>
                    </div>
                  </td>
                </tr>
              </template>
              <template v-slot:[`item.platform_name`]="{ item }">
                <v-chip size="small" variant="tonal">{{ platformDisplayName(item.platform_name) }}</v-chip>
              </template>
              <template v-slot:[`item.web_method`]="{ item }">
                <v-chip size="small" variant="tonal">{{ webMethodLabel(item) }}</v-chip>
              </template>
              <template v-slot:[`item.search_depth`]="{ item }">
                <span class="text-right">{{ item._group ? item.search_depth : ((item.extra_attributes && item.extra_attributes.search_depth) || '—') }}</span>
              </template>
              <template v-slot:[`item.source_type`]="{ item }">
                <v-icon size="small" :color="sourceTypeMeta(item.source_type).color" class="mr-1">
                  {{ sourceTypeMeta(item.source_type).icon }}
                </v-icon>
                {{ sourceTypeMeta(item.source_type).label }}
              </template>
              <template v-slot:[`item.network_name`]="{ item }">
                <v-chip v-if="item.is_aggregator" size="small" variant="tonal" color="deep-orange" title="Агрегатор: не участвует в кластеризации сетей">агрегатор</v-chip>
                <v-chip v-else-if="item.network_name" size="small" variant="tonal" color="deep-purple" :to="$store.getters.hasPermission('feature:osint') ? { path: '/monitoring/networks', query: { network: item.network_id } } : undefined" @click.stop>{{ item.network_name }}</v-chip>
                <span v-else class="text-caption text-grey">—</span>
              </template>
              <template v-slot:[`item.identifier`]="{ item }">
                <v-tooltip v-if="isOnionSource(item)" text="Onion-сайт: сбор через Tor, только текст без медиа" location="top">
                  <template #activator="{ props }">
                    <v-chip v-bind="props" size="x-small" color="deep-purple" variant="tonal" class="mr-1">
                      <v-icon start size="x-small">mdi-incognito</v-icon>Tor
                    </v-chip>
                  </template>
                </v-tooltip>
                <span class="font-weight-medium">{{ item.identifier }}</span>
                <!-- a source of a team: who added it (task 59) -->
                <div v-if="item.team_id && item.created_by_name" class="text-caption text-medium-emphasis">добавил {{ item.created_by_name }}</div>
              </template>
              <template v-slot:[`item.logo`]="{ item }">
                <v-avatar size="40">
                  <v-img v-if="item.thumbnail_url" :src="item.thumbnail_url" cover></v-img>
                  <v-icon v-else color="grey-lighten-1">mdi-cancel</v-icon>
                </v-avatar>
              </template>
              <template v-slot:[`item.display_name`]="{ item }">
                {{ item.display_name || '' }}
              </template>
              <template v-slot:[`item.description`]="{ item }">
                <v-tooltip v-if="item.description" :text="item.description" location="top" max-width="480">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">{{ truncateDescription(item.description) }}</span>
                  </template>
                </v-tooltip>
              </template>
              <template v-slot:[`item.tags`]="{ item }">
                <v-chip v-for="tag in (item.tags || [])" :key="tag" size="x-small" variant="tonal" class="mr-1">{{ tag }}</v-chip>
              </template>
              <template v-slot:[`item.messages_count`]="{ item }">
                <div
                  class="text-right"
                  :class="{ 'text-primary cursor-pointer': getMessagesCount(item) > 0 }"
                  @click="getMessagesCount(item) > 0 && openMessagesModal(item)"
                >
                  {{ formatNumber(getMessagesCount(item)) }}
                </div>
              </template>
              <template v-slot:[`item.last_content_at`]="{ item }">
                <!-- дата последней новости (published_at), без неё — дата сбора (collected_at) -->
                <span v-if="getLastContentAt(item)">{{ formatISODate(getLastContentAt(item)) }}</span>
                <span v-else class="text-caption text-grey">—</span>
              </template>
              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatISODate(item.created_at) }}
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip v-if="item._group" :color="item.active_count === 0 ? 'grey' : (item.active_count === item.items.length ? 'success' : 'warning')" size="small" variant="tonal">
                  {{ item.active_count }} из {{ item.items.length }} активны
                </v-chip>
                <!-- Web sources: 3-state health (collecting / no new lately / failing) -->
                <v-tooltip v-else-if="webHealth(item)" :text="webHealth(item).tooltip" location="top">
                  <template #activator="{ props }">
                    <v-chip v-bind="props" :color="webHealth(item).color" size="small" variant="tonal">
                      {{ webHealth(item).label }}
                    </v-chip>
                  </template>
                </v-tooltip>
                <v-chip v-else :color="statusColor(item.status)" size="small" variant="tonal">
                  <v-progress-circular v-if="item.status === 'pending' && item.source_type === 'web_feed'" indeterminate size="12" width="2" class="mr-1" />
                  {{ statusLabel(item.status, item.source_type) }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <template v-if="item._group">
                  <template v-if="isAdmin">
                    <v-tooltip v-if="item.active_count > 0" text="Остановить во всех поисковиках" location="top">
                      <template #activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-stop" size="small" variant="text" color="warning" @click="stopGroup(item)"></v-btn>
                      </template>
                    </v-tooltip>
                    <v-tooltip v-else text="Запустить во всех поисковиках" location="top">
                      <template #activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-play" size="small" variant="text" color="success" @click="startGroup(item)"></v-btn>
                      </template>
                    </v-tooltip>
                  </template>
                  <v-btn icon="mdi-pencil" size="small" variant="text" title="Поисковики и глубина" @click="openGroupEdit(item)"></v-btn>
                  <v-btn icon="mdi-delete" size="small" variant="text" color="error" title="Удалить запрос во всех поисковиках" @click="confirmDelete(item)"></v-btn>
                </template>
                <template v-else-if="isAdmin">
                  <v-tooltip v-if="item.is_monitoring" text="Остановить мониторинг" location="top">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-stop" size="small" variant="text" color="warning" @click="stopMonitoring(item)"></v-btn>
                    </template>
                  </v-tooltip>
                  <v-tooltip v-else text="Запустить мониторинг" location="top">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-play" size="small" variant="text" color="success" :disabled="!item.source_id" @click="startMonitoring(item)"></v-btn>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Обновить информацию" location="top">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-information" size="small" variant="text" :disabled="!item.source_id || item.is_monitoring || item.is_processing" @click="refreshInfo(item)"></v-btn>
                    </template>
                  </v-tooltip>
                </template>
                <v-tooltip v-if="!item._group && isTelegramPlatform(item) && item.source_id" text="Граф связей" location="top">
                  <template #activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-graph-outline" size="small" variant="text" color="primary" @click="openGraph(item)"></v-btn>
                  </template>
                </v-tooltip>
                <v-btn v-if="!item._group" icon="mdi-pencil" size="small" variant="text" @click="openEditDialog(item)"></v-btn>
                <v-btn v-if="!item._group" icon="mdi-delete" size="small" variant="text" color="error" @click="confirmDelete(item)"></v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-8">
                  <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-account-star-outline</v-icon>
                  <div class="text-body-1 text-grey">Источников пока нет</div>
                  <div class="text-caption text-grey">Добавьте аккаунты или поисковые запросы</div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters panel (right side, like Telegram) -->
      <v-col v-if="panelVisible" cols="2" key="filters">
        <v-card>
          <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
          <v-card-text class="pa-3">
            <div v-if="tab !== 'web'" class="mb-4">
              <div class="text-subtitle-2 mb-2">Платформа</div>
              <v-select
                v-model="filters.platform_id"
                :items="platforms"
                item-title="display_name"
                item-value="id"
                placeholder="Все"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                @update:model-value="loadSources"
              ></v-select>
            </div>
            <div v-if="tab !== 'web'" class="mb-4">
              <div class="text-subtitle-2 mb-2">Тип</div>
              <v-select
                v-model="filters.source_type"
                :items="filterSourceTypes"
                item-title="text"
                item-value="value"
                placeholder="Все"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                @update:model-value="loadSources"
              ></v-select>
            </div>
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2">Идентификатор</div>
              <v-text-field
                v-model="filters.identifier"
                placeholder="Поиск..."
                variant="outlined"
                density="compact"
                hide-details
                clearable
                @update:model-value="onFilterDebounce"
              ></v-text-field>
            </div>
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2">Название</div>
              <v-text-field
                v-model="filters.display_name"
                placeholder="Поиск..."
                variant="outlined"
                density="compact"
                hide-details
                clearable
                @update:model-value="onFilterDebounce"
              ></v-text-field>
            </div>
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2">Теги</div>
              <v-autocomplete
                v-model="filters.tags"
                :items="allUserTags"
                label="Теги"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="compact"
                hide-details
                clearable
                @update:model-value="loadSources"
              >
                <template #chip="{ props, item }">
                  <v-chip v-bind="props" :text="item.raw" closable size="small" />
                </template>
              </v-autocomplete>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500" @keydown.esc="showAddDialog = false">
      <v-card>
        <v-card-title>{{ editingSource ? 'Редактирование' : 'Добавить источник' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="addError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="addError = ''">{{ addError }}</v-alert>
          <v-alert v-if="addInfo" type="info" variant="tonal" density="compact" class="mb-3">{{ addInfo }}</v-alert>
          <v-select v-if="addForm.source_type !== 'web_feed' && !isGroupSearchForm" v-model="addForm.platform_id" :items="addPlatformItems" item-title="display_name" item-value="id" label="Платформа" variant="outlined" density="compact"></v-select>
          <v-select v-if="!addSearchMode" v-model="addForm.source_type" :items="addTypeItems" item-title="text" item-value="value" label="Тип" variant="outlined" density="compact" :disabled="!!editingSource || (addForm.source_type !== 'web_feed' && !addSearchAllowed)" ></v-select>
          <v-text-field
            v-if="addForm.source_type !== 'web_feed'"
            v-model="addForm.identifier"
            :label="identifierLabel"
            :placeholder="identifierPlaceholder"
            :maxlength="isSearchForm ? searchQueryMax : 500"
            :counter="isSearchForm ? searchQueryMax : false"
            :hint="identifierHint"
            :persistent-hint="!!identifierHint"
            variant="outlined" density="compact"
            :class="{ 'mb-6': !!identifierHint }"
            :disabled="!!editingSource || !!editingGroup"
            @blur="checkSource"
          ></v-text-field>
          <v-select
            v-if="isOkSearchForm"
            v-model="addForm.ok_search_mode"
            :items="okSearchModes" item-title="text" item-value="value"
            label="Раздел поиска"
            hint="Где искать в Одноклассниках"
            persistent-hint
            variant="outlined" density="compact"
            class="mb-2"
          ></v-select>
          <v-text-field
            v-if="isEngineSearchForm"
            v-model.number="addForm.search_depth"
            type="number" :min="1" :max="1000"
            label="Глубина: сколько верхних результатов забирать за прогон"
            hint="Общая для всех отмеченных поисковиков, от 1 до 1000"
            persistent-hint
            variant="outlined" density="compact"
            class="mb-3"
          ></v-text-field>
          <!-- Поисковики: флажок и под ним собственные настройки этого поисковика.
               В правке одной записи список состоит из одного поисковика, флажок заблокирован. -->
          <div v-if="isEngineSearchForm" class="mb-2">
            <div class="text-caption text-grey mb-1">Поисковики и их настройки</div>
            <div v-for="pl in formEngines" :key="pl.id" class="engine-block">
              <v-checkbox
                v-model="addForm.engine_ids" :value="pl.id" :label="pl.display_name"
                :disabled="!isGroupSearchForm"
                density="compact" hide-details class="mt-0"
              ></v-checkbox>
              <div v-if="(addForm.engine_ids || []).includes(pl.id) && ['google', 'yandex'].includes(pl.name)" class="engine-settings">
                <template v-if="pl.name === 'google'">
                  <v-row dense>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="addForm.search_language"
                        :items="searchLanguageOptions" item-title="text" item-value="value"
                        label="Язык результатов" variant="outlined" density="compact" clearable hide-details
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                        v-model="addForm.search_location"
                        :items="searchLocationOptions" item-title="text" item-value="value"
                        label="Страна поиска" variant="outlined" density="compact" clearable hide-details
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </template>
                <template v-else-if="pl.name === 'yandex'">
                  <v-row dense>
                    <v-col cols="6">
                      <v-select
                        v-model="addForm.yandex_search_type"
                        :items="yandexSearchTypeOptions" item-title="text" item-value="value"
                        label="Домен поиска" variant="outlined" density="compact" hide-details
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        v-model="addForm.search_l10n"
                        :items="yandexL10nOptions" item-title="text" item-value="value"
                        label="Язык результатов" variant="outlined" density="compact" hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-autocomplete
                    v-model="addForm.yandex_region"
                    :items="yandexRegionOptions" item-title="title" item-value="value"
                    label="Регион поиска" variant="outlined" density="compact" clearable hide-details
                    class="mt-2"
                  ></v-autocomplete>
                  <!-- Yandex Search API takes deferred queries only at night -->
                  <div class="text-caption text-medium-emphasis mt-2">
                    Яндекс собирает результаты ночью, с 00:00 до 06:00 по Москве: первые результаты появятся утром.
                  </div>
                </template>
              </div>
            </div>
          </div>
          <v-text-field
            v-if="editingSource && !addSearchMode"
            v-model="addForm.display_name"
            label="Отображаемое имя (необязательно)"
            placeholder="Автоматически подгружается с платформы"
            persistent-placeholder
            variant="outlined"
            density="compact"
          ></v-text-field>
          <v-autocomplete
            v-model="addForm.tags"
            :items="tagSuggestions"
            item-title="name"
            item-value="id"
            label="Теги"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="compact"
            return-object
            clearable
            @update:search="searchTags"
            @keydown.enter="handleTagEnter"
            @update:model-value="onTagSelected"
            ref="tagAutocomplete"
          >
            <template #chip="{ props, item }">
              <v-chip v-bind="props" :text="getTagName(item)" closable size="small" :color="item.raw.id ? 'primary' : 'success'" />
            </template>
          </v-autocomplete>
          <v-select
            v-if="isAdmin && editingSource && !addSearchMode"
            v-model="addForm.status"
            :items="statusOptions"
            item-title="text"
            item-value="value"
            label="Статус"
            variant="outlined"
            density="compact"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showAddDialog = false">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" @click="doAddOrEdit" :loading="addLoading">{{ editingSource ? 'Сохранить' : 'Добавить' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Dialog -->
    <v-dialog v-model="showBulkDialog" max-width="700" @keydown.esc="showBulkDialog = false">
      <v-card>
        <v-card-title>Групповое добавление</v-card-title>
        <v-card-text>
          <v-alert v-if="bulkError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="bulkError = ''">{{ bulkError }}</v-alert>
          <v-alert v-if="bulkResult" type="success" variant="tonal" density="compact" class="mb-3">{{ bulkResult }}</v-alert>
          <v-row>
            <v-col cols="6">
              <v-select v-model="bulkForm.platform_id" :items="platforms" item-title="display_name" item-value="id" label="Платформа" variant="outlined" density="compact"></v-select>
            </v-col>
            <v-col cols="6">
              <v-select v-model="bulkForm.source_type" :items="bulkTypeItems" item-title="text" item-value="value" label="Тип" variant="outlined" density="compact" :disabled="!bulkSearchAllowed" :hint="bulkSearchAllowed ? '' : 'Поиск доступен только на X'" persistent-hint></v-select>
            </v-col>
          </v-row>
          <v-autocomplete
            v-model="bulkForm.tags"
            :items="tagSuggestions"
            item-title="name"
            item-value="id"
            label="Общие теги (для всех)"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="compact"
            return-object
            clearable
            @update:search="searchTags"
            @keydown.enter="handleTagEnter"
            @update:model-value="onTagSelected"
            class="mb-2"
          >
            <template #chip="{ props, item }">
              <v-chip v-bind="props" :text="getTagName(item)" closable size="small" :color="item.raw.id ? 'primary' : 'success'" />
            </template>
          </v-autocomplete>
          <v-textarea v-model="bulkText" label="Источники (по одному на строку)" variant="outlined" density="compact" rows="8" placeholder="elonmusk | технологии, космос
spacex
#ukraine | политика"></v-textarea>
          <div class="text-caption text-grey">Формат: <code>идентификатор</code> или <code>идентификатор | теги</code>. Имя и фото подтягиваются с платформы автоматически.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showBulkDialog = false">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" @click="doBulkAdd" :loading="bulkLoading">Добавить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete -->
    <!-- Web Source Dialog -->
    <AddWebSourceDialog
      v-model="showWebDialog"
      :edit-source="editWebSource"
      @created="onWebSourceCreated"
      @updated="onWebSourceUpdated"
    />

    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удаление</v-card-title>
        <v-card-text>
          Удалить <strong>{{ deleteTarget?.identifier }}</strong><span v-if="deleteTarget?._group"> во всех поисковиках ({{ deleteTarget.items.length }})</span>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">{{ snackbar.text }}</v-snackbar>

    <SourceContentModal
      v-model="messagesModal.visible"
      :source-id="messagesModal.sourceId"
      :title="messagesModal.title"
      :highlight-term="messagesModal.highlightTerm"
      items-label="сообщений"
    />
      <ChannelGraphModal
      v-model="showGraph"
      :source-id="graphTarget.sourceId"
      :username="graphTarget.username"
      :title="graphTarget.title"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import apiClient from '@/apiClient';
import apiDataClient from '@/apiDataClient';
import ChannelGraphModal from '@/components/ChannelGraphModal.vue';
import SourceContentModal from '@/components/SourceContentModal.vue';
import AddWebSourceDialog from '@/components/projects/form/AddWebSourceDialog.vue';

const PANEL_KEY = 'my-sources-filters-panel';
const FILTERS_KEY = 'my-sources-filters';
const EMPTY_FILTERS = { platform_id: null, source_type: null, identifier: '', display_name: '', tags: [] };

function savedFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
    return { ...EMPTY_FILTERS, ...(saved && typeof saved === 'object' ? saved : {}) };
  } catch (e) {
    return { ...EMPTY_FILTERS };
  }
}

export default {
  name: 'MySources',
  components: { SourceContentModal, AddWebSourceDialog, ChannelGraphModal },
  data() {
    return {
      sources: [],
      platforms: [],
      total: 0,
      isLoading: false,
      panelVisible: (() => { try { return localStorage.getItem(PANEL_KEY) === 'true'; } catch (e) { return false; } })(),
      filterTimeout: null,
      pendingPollTimer: null,
      filters: savedFilters(),
      allSourcesRaw: [],  // unfiltered, for extracting tags
      // Вкладка: 'social' (аккаунты+X-поиск) | 'web' (сайты) | 'search' (поисковики).
      tab: (this.$route && ['web', 'search', 'telegram'].includes(this.$route.query.tab)) ? this.$route.query.tab : 'social',
      showGraph: false,
      graphTarget: { sourceId: null, username: '' },
      messagesCount: {},  // source_id → count
      lastContentAt: {},  // source_id → ISO дата последней новости / сбора (count-by-sources)
      messagesModal: { visible: false, sourceId: null, title: '', highlightTerm: '' },
      sourceTypes: [
        { text: 'Аккаунт', value: 'account' },
        { text: 'Поисковый запрос', value: 'search_query' },
      ],
      // SERP language/country for Google search sources (Serpent API `language`/`country`).
      searchLanguageOptions: [
        { text: 'Русский', value: 'ru' },
        { text: 'English', value: 'en' },
        { text: 'Українська', value: 'uk' },
        { text: 'Deutsch', value: 'de' },
        { text: 'Polski', value: 'pl' },
        { text: 'Français', value: 'fr' },
        { text: 'Español', value: 'es' },
      ],
      // Serpent API `country` (ISO code, supports Russia). Загружается с
      // /reference/serp/google; ниже — фолбэк. {text,value=ISO}.
      searchLocationOptions: [
        { text: 'Россия', value: 'ru' }, { text: 'США', value: 'us' },
        { text: 'Великобритания', value: 'gb' }, { text: 'Германия', value: 'de' },
        { text: 'Украина', value: 'ua' }, { text: 'Казахстан', value: 'kz' },
      ],
      // Yandex-справочники (загружаются с /reference/serp/yandex в
      // loadSerpReference; ниже — фолбэк на случай сбоя запроса).
      // searchType → search_language, l10n → search_l10n, region → search_location.
      yandexSearchTypeOptions: [
        { text: 'Россия', value: 'SEARCH_TYPE_RU' },
        { text: 'Турция', value: 'SEARCH_TYPE_TR' },
        { text: 'Международный', value: 'SEARCH_TYPE_COM' },
        { text: 'Казахстан', value: 'SEARCH_TYPE_KK' },
        { text: 'Беларусь', value: 'SEARCH_TYPE_BE' },
        { text: 'Узбекистан', value: 'SEARCH_TYPE_UZ' },
      ],
      yandexL10nOptions: [
        { text: 'Русский', value: 'LOCALIZATION_RU' },
        { text: 'Беларуская', value: 'LOCALIZATION_BE' },
        { text: 'Қазақша', value: 'LOCALIZATION_KK' },
        { text: 'Українська', value: 'LOCALIZATION_UK' },
        { text: 'Türkçe', value: 'LOCALIZATION_TR' },
        { text: 'English', value: 'LOCALIZATION_EN' },
      ],
      yandexRegionOptions: [
        { title: 'Без региона', value: '' },
        { title: 'Россия', value: '225' },
        { title: 'Москва', value: '213' },
        { title: 'Санкт-Петербург', value: '2' },
      ],
      statusOptions: [
        { text: 'Ожидание', value: 'pending' },
        { text: 'Активен', value: 'active' },
        { text: 'Не найден', value: 'not_found' },
        { text: 'Ошибка', value: 'error' },
      ],
      // Add/Edit
      showAddDialog: false,
      editingSource: null,
      addForm: { platform_id: null, source_type: 'account', identifier: '', display_name: '', tags: [], status: null, search_depth: null, search_language: null, search_location: null, search_l10n: null, engine_ids: [] },
      // true когда диалог открыт для добавления поискового запроса (Google/Яндекс)
      addSearchMode: false,
      // Предел длины поискового запроса, тот же, что проверяет core
      searchQueryMax: 400,
      okSearchModes: [
        { text: 'Группы', value: 'groups' },
        { text: 'Люди', value: 'users' },
        { text: 'Публикации', value: 'content' },
      ],
      // Групповой режим вкладки «Поисковые системы»: редактируемая группа (одна строка = один запрос)
      editingGroup: null,
      expandedSearch: [],
      addError: '', addInfo: '', addLoading: false,
      // Web source dialog (own component)
      showWebDialog: false,
      editWebSource: null,
      // Bulk
      showBulkDialog: false,
      bulkForm: { platform_id: null, source_type: 'account', tags: [] },
      bulkText: '', bulkError: '', bulkResult: '', bulkLoading: false,
      // Delete
      showDeleteDialog: false,
      deleteTarget: null,
      snackbar: { show: false, text: '', color: 'success' },
      // Tags
      tagSuggestions: [],
      currentTagSearch: '',
    };
  },
  computed: {
    ...mapGetters(['isAdmin', 'hasPermission']),
    // Имена платформ-поисковиков: Google/Яндекс всем; Ahmia (Tor) — только с правом feature:onion_sources.
    engineNames() {
      return this.hasPermission('feature:onion_sources') ? ['google', 'yandex', ...this.onionEngines] : ['google', 'yandex'];
    },
    onionEngines() {
      return ['ahmia', 'torch', 'onionland', 'tor66'];
    },
    headers() {
      if (this.tab === 'web') {
        // Сайты: платформа/тип не нужны; важны метод, здоровье, объём.
        return [
          { title: 'Название', key: 'display_name', width: '180px' },
          { title: 'Сайт', key: 'identifier', width: '220px' },
          { title: 'Метод', key: 'web_method', sortable: false, width: '140px' },
          { title: 'Сообщений', key: 'messages_count', align: 'end', width: '110px', sortRaw: this.compareMessagesCount },
          { title: 'Обновлено', key: 'last_content_at', width: '150px', sortable: false },
          { title: 'Создано', key: 'created_at', width: '140px' },
          { title: 'Теги', key: 'tags', width: '160px' },
          { title: 'Статус', key: 'status', width: '120px' },
          { title: 'Действия', key: 'actions', sortable: false, width: this.isAdmin ? '200px' : '100px' },
        ];
      }
      if (this.tab === 'search') {
        // Поисковые системы: движок, запрос, глубина, объём.
        return [
          { title: 'Запрос', key: 'identifier', width: '320px' },
          { title: 'Поисковики', key: 'engines', width: '280px', sortable: false },
          { title: 'Глубина', key: 'search_depth', align: 'end', width: '100px', sortable: false },
          { title: 'Сообщений', key: 'messages_count', align: 'end', width: '110px', sortRaw: this.compareMessagesCount },
          { title: 'Обновлено', key: 'last_content_at', width: '150px', sortable: false },
          { title: 'Создано', key: 'created_at', width: '140px' },
          { title: 'Теги', key: 'tags', width: '160px' },
          { title: 'Статус', key: 'status', width: '120px' },
          { title: 'Действия', key: 'actions', sortable: false, width: this.isAdmin ? '200px' : '100px' },
        ];
      }
      if (this.tab === 'telegram') {
        // Телеграм: только каналы, вместо платформы — сетка (кластер графа связей).
        const tg = [
          { title: 'Тип', key: 'source_type', width: '100px' },
          { title: 'Канал', key: 'identifier', width: '180px' },
          { title: '', key: 'logo', sortable: false, width: '80px' },
          { title: 'Название', key: 'display_name', width: '180px' },
          // the networks are the OSINT section: the column only for its holders (feature:osint)
          ...(this.$store.getters.hasPermission('feature:osint') ? [{ title: 'В сети', key: 'network_name', width: '160px', sortable: false }] : []),
          { title: 'Теги', key: 'tags', width: '160px' },
        ];
        // collected posts of the channel, for every user as in the common sources (18.10.2026): the
        // channel is shared, the count is its whole collection; the tariff stays a slot
        tg.push({ title: 'Сообщений', key: 'messages_count', align: 'end', width: '110px', sortRaw: this.compareMessagesCount });
        tg.push(
          { title: 'Обновлено', key: 'last_content_at', width: '150px', sortable: false },
          { title: 'Создано', key: 'created_at', width: '150px' },
          { title: 'Статус', key: 'status', width: '110px' },
          { title: 'Действия', key: 'actions', sortable: false, width: this.isAdmin ? '240px' : '140px' },
        );
        return tg;
      }
      const cols = [
        { title: 'Платформа', key: 'platform_name', width: '120px' },
        { title: 'Тип', key: 'source_type', width: '100px' },
        { title: 'Идентификатор', key: 'identifier', width: '200px' },
        { title: '', key: 'logo', sortable: false, width: '80px' },
        { title: 'Название', key: 'display_name', width: '180px' },
        { title: 'Описание', key: 'description', width: '180px', sortable: false },
        { title: 'Теги', key: 'tags', width: '180px' },
      ];
      // collected posts of the source, for every user as on the Telegram tab (29.10.2026)
      cols.push({ title: 'Сообщений', key: 'messages_count', align: 'end', width: '110px', sortRaw: this.compareMessagesCount });
      cols.push(
        { title: 'Обновлено', key: 'last_content_at', width: '150px', sortable: false },
        { title: 'Создано', key: 'created_at', width: '150px' },
        { title: 'Статус', key: 'status', width: '110px' },
        { title: 'Действия', key: 'actions', sortable: false, width: this.isAdmin ? '200px' : '100px' },
      );
      return cols;
    },
    allUserTags() {
      const tags = new Set();
      this.allSourcesRaw.forEach(s => (s.tags || []).forEach(t => tags.add(t)));
      return [...tags].sort();
    },
    // allSourcesRaw с текстовыми/тег-фильтрами, но БЕЗ разбивки по вкладкам —
    // общая основа и для видимого списка, и для счётчиков во вкладках.
    textFilteredAll() {
      let items = this.allSourcesRaw;
      if (this.filters.identifier) {
        const q = this.filters.identifier.toLowerCase();
        items = items.filter(s => s.identifier.toLowerCase().includes(q));
      }
      if (this.filters.display_name) {
        const q = this.filters.display_name.toLowerCase();
        items = items.filter(s => (s.display_name || '').toLowerCase().includes(q));
      }
      if (this.filters.tags && this.filters.tags.length) {
        const ft = this.filters.tags.map(t => t.toLowerCase());
        items = items.filter(s => (s.tags || []).some(tag => ft.includes(tag.toLowerCase())));
      }
      return items;
    },
    telegramCount() {
      return this.textFilteredAll.filter(s => this.isTelegramChannel(s)).length;
    },
    socialCount() {
      return this.textFilteredAll.filter(s => s.source_type !== 'web_feed' && !this.isEngineSearchSource(s) && !this.isTelegramChannel(s)).length;
    },
    webCount() {
      return this.textFilteredAll.filter(s => s.source_type === 'web_feed').length;
    },
    searchCount() {
      return this.textFilteredAll.filter(s => this.isEngineSearchSource(s)).length;
    },
    // Add dialog and filter both offer web sources; bulk keeps the
    // narrower sourceTypes (no web in bulk paste).
    addSourceTypes() {
      return [...this.sourceTypes, { text: 'Сайт', value: 'web_feed' }];
    },
    filterSourceTypes() {
      // На «Соцсетях» тип «Сайт» не нужен (сайты — своя вкладка).
      return this.tab === 'web' ? this.addSourceTypes : this.sourceTypes;
    },
    // «Поисковый запрос» пока поддержан только на X: в Telegram его нет и не
    // будет, в Дзене не реализован. Поэтому тип «Поиск» предлагаем/разрешаем
    // только когда выбрана платформа X, иначе тип залочен на «Аккаунт».
    xPlatformId() {
      // Внутреннее имя платформы X в ref_platforms — 'X.com' (не 'twitter');
      // допускаем и старые варианты на случай смены названия.
      const isX = pl => ['X.com', 'twitter', 'x'].includes(pl.name);
      const p = this.platforms.find(isX);
      return p ? p.id : null;
    },
    // Платформы-поисковики (Google/Яндекс) — своя вкладка «Поисковые системы».
    enginePlatforms() {
      return this.platforms.filter(pl => this.engineNames.includes(pl.name));
    },
    enginePlatformIds() {
      return this.enginePlatforms.map(pl => pl.id);
    },
    // Платформы для вкладок «Соцсети»/массового добавления — БЕЗ поисковиков.
    socialPlatforms() {
      return this.platforms.filter(pl => !['google', 'yandex', ...this.onionEngines].includes(pl.name));
    },
    // Тип «Поиск» доступен на X (соц-поиск) и на Google/Яндекс (поисковики).
    searchCapablePlatformIds() {
      const ids = [...this.enginePlatformIds];
      if (this.xPlatformId != null) ids.push(this.xPlatformId);
      if (this.okPlatformId != null) ids.push(this.okPlatformId);
      return ids;
    },
    // Одноклассники: поиск идёт по сайту под учётной записью, разделы — группы, люди, публикации
    okPlatformId() {
      const p = this.platforms.find(pl => pl.name === 'Одноклассники');
      return p ? p.id : null;
    },
    isOkSearchForm() {
      return this.addForm.source_type === 'search_query' && this.addForm.platform_id === this.okPlatformId;
    },
    // Поисковики в форме: в групповом режиме все доступные, в правке одной записи только её поисковик.
    formEngines() {
      if (this.isGroupSearchForm) return this.enginePlatforms;
      const pl = this.platforms.find(x => x.id === this.addForm.platform_id);
      return pl ? [pl] : [];
    },
    // Групповой режим формы: один запрос на несколько поисковиков (добавление или правка группы).
    isGroupSearchForm() {
      return this.addSearchMode && !this.editingSource;
    },
    // Форма сейчас в режиме поисковика (Google/Яндекс + тип «Запрос»)?
    isEngineSearchForm() {
      if (this.isGroupSearchForm) return true;
      return this.addForm.source_type === 'search_query'
        && this.enginePlatformIds.includes(this.addForm.platform_id);
    },
    // Имя движка выбранной в форме платформы ('google' | 'yandex' | null).
    selectedEngineName() {
      const p = this.platforms.find(pl => pl.id === this.addForm.platform_id);
      return p ? p.name : null;
    },
    googlePlatformId() { const p = this.platforms.find(pl => pl.name === 'google'); return p ? p.id : null; },
    yandexPlatformId() { const p = this.platforms.find(pl => pl.name === 'yandex'); return p ? p.id : null; },
    isGoogleSearchForm() {
      if (this.isGroupSearchForm) return this.googlePlatformId != null && (this.addForm.engine_ids || []).includes(this.googlePlatformId);
      return this.isEngineSearchForm && this.selectedEngineName === 'google';
    },
    isYandexSearchForm() {
      if (this.isGroupSearchForm) return this.yandexPlatformId != null && (this.addForm.engine_ids || []).includes(this.yandexPlatformId);
      return this.isEngineSearchForm && this.selectedEngineName === 'yandex';
    },
    // Вкладка «Поисковые системы»: одна строка на текст запроса, внутри записи по поисковикам.
    searchGroups() {
      const byQuery = new Map();
      for (const s of this.sources) {
        const key = (s.identifier || '').trim();
        if (!byQuery.has(key)) byQuery.set(key, []);
        byQuery.get(key).push(s);
      }
      return [...byQuery.entries()].map(([identifier, items]) => {
        const depths = [...new Set(items.map(r => (r.extra_attributes && r.extra_attributes.search_depth) || null).filter(Boolean))];
        const lastDates = items.map(r => this.getLastContentAt(r)).filter(Boolean).sort();
        const tags = [...new Set(items.flatMap(r => r.tags || []))];
        return {
          _group: true,
          id: 'group:' + identifier,
          identifier,
          items,
          search_depth: depths.length === 0 ? '—' : (depths.length === 1 ? depths[0] : depths.join('/')),
          messages_count: items.reduce((n, r) => n + this.getMessagesCount(r), 0),
          last_content_at: lastDates.length ? lastDates[lastDates.length - 1] : null,
          created_at: items.map(r => r.created_at).filter(Boolean).sort()[0] || null,
          tags,
          active_count: items.filter(r => r.is_monitoring).length,
          source_type: 'search_query',
        };
      });
    },
    isAhmiaSearchForm() {
      return this.isEngineSearchForm && this.selectedEngineName === 'ahmia';
    },
    // Список платформ в диалоге добавления: в режиме поиска — только поисковики,
    // иначе — соцсети (поисковики скрыты, у них своя вкладка).
    addPlatformItems() {
      return this.addSearchMode ? this.enginePlatforms : this.socialPlatforms;
    },
    addSearchAllowed() {
      return this.addForm.platform_id != null && this.searchCapablePlatformIds.includes(this.addForm.platform_id);
    },
    bulkSearchAllowed() {
      return this.bulkForm.platform_id != null && this.bulkForm.platform_id === this.xPlatformId;
    },
    addTypeItems() {
      // Выбрана соцсеть — «Сайт» здесь не предлагаем: это другой поток
      // (адрес, шаблоны, предпросмотр) и своя вкладка.
      const types = this.addForm.platform_id != null
        ? this.sourceTypes
        : this.addSourceTypes;
      return types.filter(t => t.value !== 'search_query' || this.addSearchAllowed);
    },
    bulkTypeItems() {
      return this.sourceTypes.filter(t => t.value !== 'search_query' || this.bulkSearchAllowed);
    },
    identifierLabel() { return this.addForm.source_type === 'search_query' ? 'Поисковый запрос' : 'Имя аккаунта / канала'; },
    isSearchForm() { return this.addForm.source_type === 'search_query'; },
    identifierHint() {
      if (!this.isEngineSearchForm) return '';
      return `Максимум ${this.searchQueryMax} символов. Лучше несколько отдельных запросов, чем сложный через ИЛИ`;
    },
    identifierPlaceholder() {
      if (this.isEngineSearchForm) return '"точная фраза" -реклама OR ключевое site:example.com';
      if (this.addForm.source_type === 'search_query') return '#SpaceX или ключевое слово';
      const p = this.platforms.find(pl => pl.id === this.addForm.platform_id);
      if (p && ['X.com', 'twitter', 'x'].includes(p.name)) return '@elonmusk или https://x.com/elonmusk';
      if (p?.name === 'telegram') return '@channel или t.me/channel';
      return '@username или URL';
    },
    filteredSources() {
      let items = this.sources;
      if (this.filters.identifier) {
        const q = this.filters.identifier.toLowerCase();
        items = items.filter(s => s.identifier.toLowerCase().includes(q));
      }
      if (this.filters.display_name) {
        const q = this.filters.display_name.toLowerCase();
        items = items.filter(s => (s.display_name || '').toLowerCase().includes(q));
      }
      return items;
    },
  },
  async mounted() {
    await this.loadPlatforms();
    await this.loadSources();
    this.loadSerpReference();
  },
  watch: {
    // the panel and the filters live between visits (localStorage, as *-filters-panel of the tables)
    panelVisible(v) {
      try { localStorage.setItem(PANEL_KEY, String(v)); } catch (e) { /* storage off: just not kept */ }
    },
    filters: {
      deep: true,
      handler(v) {
        try { localStorage.setItem(FILTERS_KEY, JSON.stringify(v)); } catch (e) { /* not kept */ }
      },
    },
    '$route.query.tab'(v) {
      const nt = ['web', 'search', 'telegram'].includes(v) ? v : 'social';
      if (nt !== this.tab) { this.tab = nt; this.loadSources(); }
    },
    // Picking "Сайт" in the unified Add dialog hands off to the web form
    // (URL + patterns + preview), which is a different flow from the
    // account/search generic form.
    'addForm.source_type'(v) {
      if (v === 'web_feed' && !this.editingSource && this.showAddDialog) {
        this.showAddDialog = false;
        this.editWebSource = null;
        this.showWebDialog = true;
        this.$nextTick(() => { this.addForm.source_type = 'account'; });
      }
    },
    // Сменили платформу на не-X, а был выбран «Поиск» — вернуть на «Аккаунт»
    // (поиск поддержан только на X).
    'addForm.platform_id'() {
      if (this.addForm.source_type === 'search_query' && !this.addSearchAllowed) {
        this.addForm.source_type = 'account';
      }
      // Переключение движка Google↔Яндекс в режиме добавления: гео/язык слоты
      // семантически разные (Google language/country vs Yandex
      // searchType/region), поэтому сбрасываем на дефолты нового движка, чтобы
      // не утащить, напр., 'United States' в поле региона Яндекса.
      if (this.addSearchMode && !this.editingSource && this.isEngineSearchForm) {
        const def = this.engineSearchDefaults(this.selectedEngineName);
        this.addForm.search_language = def.language;
        this.addForm.search_location = def.location;
        this.addForm.search_l10n = def.l10n;
      }
    },
    'bulkForm.platform_id'() {
      if (this.bulkForm.source_type === 'search_query' && !this.bulkSearchAllowed) {
        this.bulkForm.source_type = 'account';
      }
    },
  },
  beforeUnmount() {
    if (this.pendingPollTimer) clearTimeout(this.pendingPollTimer);
    if (this.filterTimeout) clearTimeout(this.filterTimeout);
  },
  methods: {
    async loadSerpReference() {
      // Google (Serpent API): languages (ISO2) + countries (ISO, incl. Russia).
      // Yandex: static searchType / l10n / regions. Keep built-in fallbacks on error.
      try {
        const { data } = await apiClient.get('/reference/serp/google');
        if (data.languages && data.languages.length) {
          this.searchLanguageOptions = data.languages.map(l => ({ text: l.name, value: l.code }));
        }
        const countries = data.countries || data.locations;
        if (countries && countries.length) {
          this.searchLocationOptions = countries.map(c => ({ text: c.name, value: c.code }));
        }
      } catch (e) { /* keep fallback options */ }
      try {
        const { data } = await apiClient.get('/reference/serp/yandex');
        if (data.search_types && data.search_types.length) {
          this.yandexSearchTypeOptions = data.search_types.map(s => ({ text: s.name, value: s.value }));
        }
        if (data.l10n && data.l10n.length) {
          this.yandexL10nOptions = data.l10n.map(s => ({ text: s.name, value: s.value }));
        }
        if (data.regions && data.regions.length) {
          this.yandexRegionOptions = data.regions.map(r => ({ title: r.name, value: r.id }));
        }
      } catch (e) { /* keep fallback options */ }
    },
    async loadPlatforms() {
      try {
        const all = (await apiClient.get('/reference/platforms')).data.items || [];
        // Only show platforms that:
        //  - have a display name (filter blank entries like Facebook/Instagram
        //    which exist in the catalog but aren't user-facing here)
        //  - are actively monitored (no point letting the user add a source on
        //    a platform we don't scrape)
        //  - are NOT user_web — that one has its own "Сайт" button + dialog,
        //    the generic Account/Search form doesn't apply.
        this.platforms = all.filter(p =>
          p?.display_name
          && p.is_monitoring
          && p.name !== 'user_web'
        );
      } catch { this.platforms = []; }
    },
    setTab(v) {
      if (v === this.tab) return;
      this.tab = v;
      // deep-link (пункты меню ведут на ?tab=...)
      this.$router.replace({ query: { ...this.$route.query, tab: v } }).catch(() => {});
      this.loadSources();
    },
    openWebAdd() {
      this.editWebSource = null;
      this.showWebDialog = true;
    },
    // the count is not in the row (a separate map, getMessagesCount): sort by what is shown
    compareMessagesCount(a, b) {
      return (this.getMessagesCount(a) || 0) - (this.getMessagesCount(b) || 0);
    },
    async loadSources() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filters.platform_id) params.platform_id = this.filters.platform_id;
        if (this.filters.source_type) params.source_type = this.filters.source_type;
        // all of the user's sources, page by page: the endpoint returns 50 by default, and the
        // tabs, their counters and sorting work on the whole list
        const PAGE = 500;
        const items = [];
        for (let skip = 0; ; skip += PAGE) {
          const resp = await apiClient.get('/user-sources', { params: { ...params, skip, limit: PAGE } });
          const page = resp.data.items || [];
          items.push(...page);
          if (page.length < PAGE) break;
        }
        this.allSourcesRaw = items;
        // Текстовые фильтры — в computed textFilteredAll. Здесь — разбивка по
        // вкладке: Сайты = web_feed; Поисковые системы = search_query на
        // Google/Яндекс; Соцсети = остальное (аккаунты + X-поиск).
        const filtered = this.textFilteredAll.filter(s => {
          if (this.tab === 'web') return s.source_type === 'web_feed';
          if (this.tab === 'search') return this.isEngineSearchSource(s);
          if (this.tab === 'telegram') return this.isTelegramChannel(s);
          // Соцсети = остальное без Telegram-каналов (у них своя вкладка)
          return s.source_type !== 'web_feed' && !this.isEngineSearchSource(s) && !this.isTelegramChannel(s);
        });
        this.sources = filtered;
        this.total = filtered.length;
        // Счётчик сообщений: на «Сайтах» и «Поисковых системах» источники
        // per-user изолированы — счётчик виден владельцу (см. wiki/billing).
        // На «Телеграм» — всем с 18.10.2026 (общий сбор канала, тариф — слот);
        // на «Соцсетях» — тоже всем с 29.10.2026. Дата «Обновлено» нужна всем.
        this.fetchMessageCounts(filtered);
        this.schedulePendingPoll(items);
      } catch (e) { console.error(e); } finally { this.isLoading = false; }
    },
    schedulePendingPoll(items) {
      // While any web source is still in background discovery, refresh the
      // list so its chip flips to Активен/Ошибка without a manual reload.
      if (this.pendingPollTimer) { clearTimeout(this.pendingPollTimer); this.pendingPollTimer = null; }
      const hasPending = (items || []).some(s => s.source_type === 'web_feed' && s.status === 'pending');
      if (hasPending) {
        this.pendingPollTimer = setTimeout(() => this.loadSources(), 4000);
      }
    },
    async fetchMessageCounts(items) {
      const ids = (items || [])
        .map(s => s.source_id)
        .filter(id => !!id);
      if (!ids.length) return;
      try {
        const { data } = await apiDataClient.get('/source-content/count-by-sources', {
          params: { source_ids: ids.join(',') },
        });
        this.messagesCount = { ...this.messagesCount, ...(data.counts || {}) };
        this.lastContentAt = { ...this.lastContentAt, ...(data.last_content_at || {}) };
      } catch (e) {
        console.error('fetchMessageCounts error:', e);
      }
    },
    getLastContentAt(item) {
      if (item && item._group) {
        // Группа запросов: самая свежая дата среди записей по поисковикам
        const dates = item.items.map(r => this.getLastContentAt(r)).filter(Boolean).sort();
        return dates.length ? dates[dates.length - 1] : null;
      }
      if (!item || !item.source_id) return null;
      return this.lastContentAt[item.source_id] || null;
    },
    getMessagesCount(item) {
      if (item && item._group) {
        // Группа запросов: сумма по записям всех поисковиков
        return item.items.reduce((n, r) => n + this.getMessagesCount(r), 0);
      }
      if (!item || !item.source_id) return 0;
      return this.messagesCount[item.source_id] || 0;
    },
    openMessagesModal(item) {
      if (this.getMessagesCount(item) === 0) return;
      if (item._group) {
        // Группа запросов: сообщения по всем поисковикам сразу (список id через запятую)
        const ids = item.items.map(r => r.source_id).filter(Boolean);
        if (!ids.length) return;
        this.messagesModal.sourceId = ids.join(',');
        this.messagesModal.title = item.identifier;
        this.messagesModal.highlightTerm = item.identifier || '';
        this.messagesModal.visible = true;
        return;
      }
      if (!item.source_id) return;
      this.messagesModal.sourceId = item.source_id;
      this.messagesModal.title = item.display_name || item.identifier;
      // For search sources, highlight the query text itself in each tweet.
      this.messagesModal.highlightTerm = item.source_type === 'search_query'
        ? (item.identifier || '')
        : '';
      this.messagesModal.visible = true;
    },
    truncateDescription(text) {
      if (!text) return '';
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    },
    formatNumber(n) {
      if (n == null) return '0';
      return Number(n).toLocaleString('ru-RU');
    },
    formatISODate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime()) || d.getFullYear() === 1970) return '';
      const pad = (x) => String(x).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    onFilterDebounce() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => this.loadSources(), 300);
    },
    // Источник-поисковик (Google/Яндекс + тип «Запрос») — вкладка «Поисковые системы».
    isTelegramPlatform(s) {
      // ref_platforms.name = «Telegram» (с заглавной), id = 7 — сравниваем без учёта регистра
      return s.platform_id === 7 || (s.platform_name || '').toLowerCase() === 'telegram';
    },
    isTelegramChannel(s) {
      return this.isTelegramPlatform(s) && s.source_type !== 'web_feed' && !this.isEngineSearchSource(s);
    },
    openGraph(item) {
      this.graphTarget = { sourceId: item.source_id, username: item.identifier, title: item.display_name || '' };
      this.showGraph = true;
    },
    isEngineSearchSource(s) {
      return s.source_type === 'search_query' && this.enginePlatformIds.includes(s.platform_id);
    },
    openAddDialog() {
      this.editingSource = null;
      this.addSearchMode = false;
      this.addForm = { platform_id: null, source_type: 'account', identifier: '', display_name: '', tags: [], status: null, search_depth: null, ok_search_mode: 'groups' };
      this.addError = ''; this.addInfo = '';
      this.showAddDialog = true;
    },
    // Дефолты гео/языка по движку (Google: Serpent language+country, l10n не
    // используется; Яндекс: searchType→language, region→location, l10n→l10n).
    // Onion-источник (Tor): по адресу .onion (v3) или флагу is_onion в discovery_info.
    isOnionSource(item) {
      const url = (item && item.identifier) || '';
      return !!(item && item.discovery_info && item.discovery_info.is_onion) || /\.onion(\/|$)/i.test(url);
    },
    engineSearchDefaults(engineName) {
      if (this.onionEngines.includes(engineName)) {
        return { language: null, location: null, l10n: null }; // у onion-поисковиков нет языка/региона
      }
      if (engineName === 'yandex') {
        return { language: 'SEARCH_TYPE_RU', location: '', l10n: 'LOCALIZATION_RU' };
      }
      return { language: 'ru', location: 'ru', l10n: null };
    },
    openSearchAdd() {
      // Один запрос на несколько поисковиков: текст, флажки поисковиков, общая глубина,
      // язык/регион только для Google и Яндекса. На каждый поисковик создаётся своя запись.
      this.editingSource = null;
      this.editingGroup = null;
      this.addSearchMode = true;
      const firstEngine = this.enginePlatforms[0];
      this.addForm = {
        platform_id: null,
        source_type: 'search_query',
        identifier: '', display_name: '', tags: [], status: null,
        search_depth: 10, search_language: 'ru', search_location: 'ru', search_l10n: 'LOCALIZATION_RU',
        yandex_search_type: 'SEARCH_TYPE_RU', yandex_region: '',
        engine_ids: firstEngine ? [firstEngine.id] : [],
      };
      this.addError = ''; this.addInfo = '';
      if (!firstEngine) { this.addInfo = 'Платформы поисковиков не настроены'; }
      this.showAddDialog = true;
    },
    // Правка группы: набор поисковиков (снятый флажок удаляет запись, новый создаёт) и общая глубина.
    openGroupEdit(group) {
      this.editingSource = null;
      this.editingGroup = group;
      this.addSearchMode = true;
      const google = group.items.find(r => r.platform_id === this.googlePlatformId);
      const yandex = group.items.find(r => r.platform_id === this.yandexPlatformId);
      const depths = group.items.map(r => (r.extra_attributes && r.extra_attributes.search_depth) || null).filter(Boolean);
      this.addForm = {
        platform_id: null,
        source_type: 'search_query',
        identifier: group.identifier, display_name: '', tags: group.tags.map(t => ({ name: t })), status: null,
        search_depth: depths.length ? Math.max(...depths) : 10,
        search_language: (google && google.extra_attributes && google.extra_attributes.search_language) || 'ru',
        search_location: (google && google.extra_attributes && google.extra_attributes.search_location) || 'ru',
        search_l10n: (yandex && yandex.extra_attributes && yandex.extra_attributes.search_l10n) || 'LOCALIZATION_RU',
        engine_ids: group.items.map(r => r.platform_id),
      };
      // У Яндекса поле search_language хранит тип поиска, а search_location регион; в форме это отдельные поля
      this.addForm.yandex_search_type = (yandex && yandex.extra_attributes && yandex.extra_attributes.search_language) || 'SEARCH_TYPE_RU';
      this.addForm.yandex_region = (yandex && yandex.extra_attributes && yandex.extra_attributes.search_location) || '';
      this.addError = ''; this.addInfo = '';
      this.showAddDialog = true;
    },
    // Настройки для записи одного поисковика из общей формы группы.
    engineKnobs(platformId) {
      const name = (this.platforms.find(pl => pl.id === platformId) || {}).name;
      if (name === 'google') return { search_language: this.addForm.search_language || null, search_location: this.addForm.search_location || null, search_l10n: null };
      if (name === 'yandex') return { search_language: this.addForm.yandex_search_type || 'SEARCH_TYPE_RU', search_location: this.addForm.yandex_region || null, search_l10n: this.addForm.search_l10n || 'LOCALIZATION_RU' };
      return { search_language: null, search_location: null, search_l10n: null };
    },
    engineChipColor(rec) {
      if (rec.status === 'error') return 'error';
      return rec.is_monitoring ? 'success' : 'grey';
    },
    engineChipTooltip(rec) {
      const st = rec.status === 'error' ? 'ошибка' : (rec.is_monitoring ? 'мониторится' : 'остановлен');
      return `${this.platformDisplayName(rec.platform_name)}: ${st}, сообщений ${this.formatNumber(this.getMessagesCount(rec))}`;
    },
    async startGroup(group) { for (const rec of group.items) if (!rec.is_monitoring) await this.startMonitoring(rec); },
    async stopGroup(group) { for (const rec of group.items) if (rec.is_monitoring) await this.stopMonitoring(rec); },
    openEditDialog(source) {
      // Web sources have their own dialog (URL + article patterns), the
      // generic Account/Search form can't represent them.
      if (source.source_type === 'web_feed') {
        this.editWebSource = source;
        this.showWebDialog = true;
        return;
      }
      this.editingSource = source;
      this.addSearchMode = source.source_type === 'search_query'
        && this.enginePlatformIds.includes(source.platform_id);
      this.addForm = {
        platform_id: source.platform_id,
        source_type: source.source_type,
        identifier: source.identifier,
        display_name: source.display_name || '',
        tags: (source.tags || []).map(t => ({ name: t })),
        status: source.status,
        search_depth: (source.extra_attributes && source.extra_attributes.search_depth) || null,
        search_language: (source.extra_attributes && source.extra_attributes.search_language) || null,
        search_location: (source.extra_attributes && source.extra_attributes.search_location) || null,
        search_l10n: (source.extra_attributes && source.extra_attributes.search_l10n) || null,
        ok_search_mode: (source.extra_attributes && source.extra_attributes.ok_search_mode) || 'groups',
        // Яндекс хранит тип поиска в search_language и регион в search_location; в форме это отдельные поля
        yandex_search_type: (source.extra_attributes && source.extra_attributes.search_language) || 'SEARCH_TYPE_RU',
        yandex_region: (source.extra_attributes && source.extra_attributes.search_location) || '',
        engine_ids: [source.platform_id],
      };
      this.addError = ''; this.addInfo = '';
      this.showAddDialog = true;
    },
    async checkSource() {
      if (!this.addForm.platform_id || !this.addForm.identifier) return;
      if (this.isEngineSearchForm) return; // поисковики: проверка по клику «Сохранить» (свой дубликат)
      try {
        const r = await apiClient.get('/user-sources/check', { params: { platform_id: this.addForm.platform_id, identifier: this.addForm.identifier } });
        this.addInfo = r.data.exists ? (r.data.is_monitoring ? 'Уже мониторится' : 'Найден в базе') : 'Новый — будет создан';
      } catch { this.addInfo = ''; }
    },
    async doAddOrEdit() {
      this.addLoading = true; this.addError = '';
      try {
        if (this.editingSource) {
          const tagNames = (this.addForm.tags || []).map(t => t.name || t);
          const payload = {
            platform_id: this.addForm.platform_id,
            source_type: this.addForm.source_type,
            display_name: this.addForm.display_name,
            tags: tagNames,
          };
          if (this.isAdmin && this.addForm.status && !this.addSearchMode) {
            payload.status = this.addForm.status;
          }
          if (this.isOkSearchForm && this.addForm.ok_search_mode) {
            payload.ok_search_mode = this.addForm.ok_search_mode;
          }
          if (this.isEngineSearchForm) {
            if (this.addForm.search_depth) payload.search_depth = this.addForm.search_depth;
            if (this.selectedEngineName === 'yandex') {
              if (this.addForm.yandex_search_type) payload.search_language = this.addForm.yandex_search_type;
              if (this.addForm.yandex_region) payload.search_location = this.addForm.yandex_region;
            } else {
              if (this.addForm.search_language) payload.search_language = this.addForm.search_language;
              if (this.addForm.search_location) payload.search_location = this.addForm.search_location;
            }
            if (this.addForm.search_l10n) payload.search_l10n = this.addForm.search_l10n;
          }
          await apiClient.put(`/user-sources/${this.editingSource.id}`, payload);
          this.showSnackbar('Сохранено');
        } else {
          if (!this.isGroupSearchForm && !this.addForm.platform_id) { this.addError = 'Выберите платформу'; this.addLoading = false; return; }
          if (!this.addForm.identifier.trim()) { this.addError = this.isGroupSearchForm ? 'Введите запрос' : 'Введите идентификатор'; this.addLoading = false; return; }
          // display_name is optional on create — backend pulls it from
          // the platform profile on first monitor. Send null if empty so
          // the server uses its '@{identifier}' placeholder.
          if (this.isGroupSearchForm) {
            const query = (this.addForm.identifier || '').trim();
            const engineIds = this.addForm.engine_ids || [];
            if (!query) { this.addError = 'Введите запрос'; return; }
            if (!engineIds.length) { this.addError = 'Выберите хотя бы один поисковик'; return; }
            const tags = (this.addForm.tags || []).map(t => t.name || t);
            const existing = this.editingGroup ? this.editingGroup.items : [];
            // 1) снятые флажки: удалить записи
            for (const rec of existing.filter(r => !engineIds.includes(r.platform_id))) {
              await apiClient.delete(`/user-sources/${rec.id}`);
            }
            // 2) оставшиеся: обновить глубину и настройки поисковика
            for (const rec of existing.filter(r => engineIds.includes(r.platform_id))) {
              const knobs = this.engineKnobs(rec.platform_id);
              await apiClient.put(`/user-sources/${rec.id}`, {
                // display_name не отправляем: в списке он подставлен из общего
                // источника, и обратная отправка записывала длинное название
                // запроса в собственное имя записи
                platform_id: rec.platform_id, source_type: 'search_query', tags,
                search_depth: this.addForm.search_depth || undefined,
                search_language: knobs.search_language || undefined,
                search_location: knobs.search_location || undefined,
                search_l10n: knobs.search_l10n || undefined,
              });
            }
            // 3) новые флажки: создать записи одним запросом (существующие пары сервер пропустит)
            const toCreate = engineIds.filter(id => !existing.some(r => r.platform_id === id));
            if (toCreate.length) {
              const resp = await apiClient.post('/user-sources/search-query', {
                query, search_depth: this.addForm.search_depth || null, tags,
                engines: toCreate.map(id => ({ platform_id: id, ...this.engineKnobs(id) })),
              });
              if (!this.editingGroup && !resp.data.created.length) {
                this.addError = 'Такой запрос уже добавлен на выбранных поисковиках';
                return;
              }
            }
            this.showAddDialog = false;
            await this.loadSources();
            return;
          }
          const payload = {
            ...this.addForm,
            display_name: this.addForm.display_name || null,
            tags: (this.addForm.tags || []).map(t => t.name || t),
          };
          await apiClient.post('/user-sources', payload);
          if (!this.isEngineSearchForm) this.showSnackbar('Добавлено');
        }
        this.showAddDialog = false;
        await this.loadSources();
      } catch (e) { this.addError = e.response?.data?.detail || 'Ошибка'; } finally { this.addLoading = false; }
    },
    openBulkDialog() {
      this.bulkForm = { platform_id: null, source_type: 'account', tags: [] };
      this.bulkText = ''; this.bulkError = ''; this.bulkResult = '';
      this.showBulkDialog = true;
    },
    async doBulkAdd() {
      if (!this.bulkForm.platform_id) { this.bulkError = 'Выберите платформу'; return; }
      const lines = this.bulkText.split('\n').map(l => l.trim()).filter(Boolean);
      if (!lines.length) { this.bulkError = 'Введите хотя бы один источник'; return; }
      const items = lines.map(line => {
        const parts = line.split('|').map(p => p.trim());
        // Format: "identifier" or "identifier | tags"
        // display_name pulled from platform automatically on first monitor.
        return { identifier: parts[0], display_name: null, tags: parts[1] ? parts[1].split(',').map(t => t.trim()).filter(Boolean) : [] };
      });
      this.bulkLoading = true; this.bulkError = ''; this.bulkResult = '';
      try {
        const bulkPayload = { ...this.bulkForm, tags: (this.bulkForm.tags || []).map(t => t.name || t), items };
        const resp = await apiClient.post('/user-sources/bulk', bulkPayload);
        const d = resp.data;
        this.bulkResult = `Добавлено: ${d.created}, пропущено: ${d.skipped}` + (d.errors?.length ? `, ошибок: ${d.errors.length}` : '');
        if (d.created > 0) await this.loadSources();
      } catch (e) { this.bulkError = e.response?.data?.detail || 'Ошибка'; } finally { this.bulkLoading = false; }
    },
    async startMonitoring(item) {
      try {
        await apiClient.post(`/user-sources/${item.id}/start-monitoring`);
        item.is_monitoring = true;
        this.showSnackbar('Мониторинг запущен');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка запуска мониторинга', 'error');
      }
    },
    async stopMonitoring(item) {
      try {
        await apiClient.post(`/user-sources/${item.id}/stop-monitoring`);
        item.is_monitoring = false;
        this.showSnackbar('Мониторинг остановлен');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка остановки мониторинга', 'error');
      }
    },
    async refreshInfo(item) {
      try {
        await apiClient.post(`/user-sources/${item.id}/refresh-info`);
        item.is_processing = true;
        this.showSnackbar('Обновление информации запущено');
      } catch (e) {
        this.showSnackbar(e.response?.data?.detail || 'Ошибка запуска обновления', 'error');
      }
    },
    confirmDelete(s) { this.deleteTarget = s; this.showDeleteDialog = true; },
    async doDelete() {
      try {
        const targets = this.deleteTarget._group ? this.deleteTarget.items : [this.deleteTarget];
        for (const rec of targets) await apiClient.delete(`/user-sources/${rec.id}`);
        this.showDeleteDialog = false; this.showSnackbar('Удалено'); await this.loadSources();
      } catch { this.showSnackbar('Ошибка', 'error'); }
    },
    platformDisplayName(n) { return { telegram: 'Telegram', twitter: 'X.com', dzen: 'Дзен', vk: 'ВК', user_web: 'Сайт' }[n] || n; },
    sourceTypeMeta(t) {
      return {
        search_query: { label: 'Поиск', icon: 'mdi-magnify', color: 'blue' },
        web_feed: { label: 'Сайт', icon: 'mdi-web', color: 'teal' },
      }[t] || { label: 'Аккаунт', icon: 'mdi-account', color: 'grey' };
    },
    async onWebSourceCreated() { await this.loadSources(); },
    async onWebSourceUpdated() { this.showSnackbar('Сохранено'); await this.loadSources(); },
    // Метка метода получения ссылок для вкладки «Сайты» (пин приоритетнее).
    webMethodLabel(item) {
      const di = item.discovery_info || {};
      const pin = item.extra_attributes && item.extra_attributes.pinned_method;
      const m = pin || di.method;
      const label = ({
        rss: 'RSS', sitemap: 'Sitemap', jsonld: 'JSON-LD', heuristic: 'Эвристика',
        user_pattern: 'Ссылки страницы', llm: 'AI', selector: 'Селектор', multi: 'Несколько',
      })[m] || m || 'Авто';
      return pin ? `${label} (закреплён)` : label;
    },
    // 3-state health for web sources, computed from discovery_info.
    // Red: the link-discovery method finds nothing / source errored.
    // Yellow: collecting fine but no NEW articles for 7+ days.
    // Green: collecting and fresh. null -> fall back to the plain status chip.
    webHealth(item) {
      const di = item.discovery_info;
      if (item.source_type !== 'web_feed' || !di || item.status === 'pending') return null;
      if (item.status === 'error' || di.last_extract_status === 'discovery_empty') {
        return {
          color: 'error', label: 'Ошибка сбора',
          tooltip: 'Метод получения ссылок не находит статьи. Проверьте настройки или переключите метод в «Авто».',
        };
      }
      if (di.redirected_to) {
        return {
          color: 'warning', label: 'Перенаправление',
          tooltip: `Адрес перенаправляет на ${di.redirected_to}: собирается эта страница, а не указанный раздел. Проверьте адрес.`,
        };
      }
      if (!di.last_check_at) return null;
      const STALE_DAYS = 7;
      // last_new_at is a fresh watermark — older sources don't have it yet;
      // don't false-alarm them, only warn when the watermark exists and is stale.
      if (di.last_new_at && (Date.now() - new Date(di.last_new_at).getTime()) > STALE_DAYS * 864e5) {
        return {
          color: 'warning', label: 'Нет новых',
          tooltip: `Сбор работает, но новых статей не было ${STALE_DAYS}+ дней.`,
        };
      }
      return { color: 'success', label: 'Работает', tooltip: 'Источник собирается штатно.' };
    },
    statusColor(s) { return { active: 'success', pending: 'grey', not_found: 'warning', error: 'error' }[s] || 'grey'; },
    statusLabel(s, sourceType) {
      // web_feed sources go through a background discovery check, so their
      // pending/error read differently from generic account sources.
      if (sourceType === 'web_feed') {
        const web = { active: 'Активен', pending: 'На проверке', not_found: 'Лента не найдена', error: 'Ошибка проверки' };
        if (web[s]) return web[s];
      }
      return { active: 'Активен', pending: 'Ожидание', not_found: 'Не найден', error: 'Ошибка' }[s] || s;
    },
    showSnackbar(text, color = 'success') { this.snackbar = { show: true, text, color }; },
    // Tags
    async searchTags(query) {
      this.currentTagSearch = query || '';
      if (!query) { this.tagSuggestions = []; return; }
      try {
        const resp = await apiClient.get('/tags', { params: { q: query } });
        this.tagSuggestions = resp.data.map(t => ({ id: t.id, name: t.name }));
      } catch (e) { this.tagSuggestions = []; }
    },
    handleTagEnter(event) {
      if (this.currentTagSearch && this.currentTagSearch.trim()) {
        event.preventDefault();
        const tagName = this.currentTagSearch.trim();
        const form = this.editingSource ? this.addForm : (this.showBulkDialog ? this.bulkForm : this.addForm);
        const tags = form.tags || [];
        const exists = tags.find(t => (t.name || t).toLowerCase() === tagName.toLowerCase());
        if (!exists) {
          tags.push({ name: tagName });
        }
        this.currentTagSearch = '';
        this.$nextTick(() => {
          if (this.$refs.tagAutocomplete) {
            try { this.$refs.tagAutocomplete.search = ''; } catch (e) { /* ignore */ }
          }
        });
      }
    },
    onTagSelected() {
      this.currentTagSearch = '';
      this.$nextTick(() => {
        if (this.$refs.tagAutocomplete) {
          try { this.$refs.tagAutocomplete.search = ''; this.$refs.tagAutocomplete.focus(); } catch (e) { /* ignore */ }
        }
      });
    },
    getTagName(item) {
      if (item.raw) return item.raw.name || item.raw;
      return item.name || item;
    },
  },
};
</script>

<style scoped>
.table-column-transition {
  transition: all 0.3s ease;
}
</style>

<style>
/* Bold table headers — same as Telegram page */
.engine-block { margin-bottom: 4px; }
.engine-settings { margin: 0 0 8px 32px; padding: 8px 12px; border-left: 2px solid rgba(0, 0, 0, 0.12); }
.v-data-table th {
  font-weight: bold !important;
}
</style>
